import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Story } from '../../../shared/types';
import { formatRating, truncateText } from '../../../shared/utils';
import { Tooltip } from '../../../shared/components/UI';

interface StoryCardProps {
  story: Story;
  showAuthor?: boolean;
  showActions?: boolean;
  onEdit?: (story: Story) => void;
  onDelete?: (story: Story) => void;
}

const CardContainer = styled(Link)<{ $hasActions: boolean }>`
  display: block;
  background: var(--color-gray-50);
  border-radius: var(--border-radius-lg);
  padding: 1.5rem;
  padding-bottom: ${props => props.$hasActions ? '2.5rem' : '1.5rem'};
  text-decoration: none;
  transition: all 0.2s ease;
  border: 1px solid var(--color-gray-200);
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
    border-color: var(--color-primary);
    text-decoration: none;
  }
`;

const StoryTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const StorySummary = styled.p`
  color: var(--color-gray-600);
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StoryMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  color: var(--color-gray-500);
`;

const StoryGenre = styled.span`
  background: var(--color-secondary);
  color: var(--color-gray-800);
  padding: 0.25rem 0.5rem;
  border-radius: var(--border-radius-sm);
  font-weight: 500;
  font-size: 0.8rem;
`;

const StoryRating = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
`;

const AuthorInfo = styled.div`
  color: var(--color-gray-500);
  font-size: 0.85rem;
  margin-bottom: 1rem;
`;

const VisibilityBadge = styled.span<{ visibility: string }>`
  background: ${props => {
    switch (props.visibility) {
      case 'PUBLIC': return 'var(--color-green-500)';
      case 'FRIENDS': return '#F59E0B';
      default: return 'var(--color-gray-500)';
    }
  }};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: var(--border-radius-sm);
  font-weight: 500;
  font-size: 0.75rem;
  text-transform: capitalize;
  margin-left: 0.5rem;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
`;

const ActionButton = styled.button<{ variant?: 'danger' }>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'danger' ? `
    background: var(--color-red-600);
    color: white;
    &:hover {
      background: var(--color-red-700);
    }
  ` : `
    background: var(--color-primary);
    color: var(--color-gray-800);
    &:hover {
      background: var(--color-primary-dark);
    }
  `}
`;

const StoryCard: React.FC<StoryCardProps> = ({
  story,
  showAuthor = true,
  showActions = false,
  onEdit,
  onDelete
}) => {
  const { t } = useTranslation();
  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEdit?.(story);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.(story);
  };

  const getVisibilityTooltip = (visibility: string) => {
    switch (visibility) {
      case 'PUBLIC':
        return 'Anyone can see this story';
      case 'FRIENDS':
        return 'Only your friends can see this story';
      case 'PRIVATE':
        return 'Only you can see this story';
      default:
        return '';
    }
  };

  const hasActions = Boolean(showActions && (onEdit || onDelete));

  return (
    <CardContainer to={`/story/${story.id}`} $hasActions={hasActions}>
      <StoryTitle>{story.title}</StoryTitle>
      
      {story.summary && (
        <StorySummary>
          {truncateText(story.summary, 150)}
        </StorySummary>
      )}
      
      {showAuthor && (
        <AuthorInfo>
          {t('home.byAuthor', { author: story.author.fullName || story.author.username })}
        </AuthorInfo>
      )}
      
      <StoryMeta>
        <div>
          <StoryGenre>{t(`genres.${story.genre}`, story.genre)}</StoryGenre>
          {showActions && (
            <Tooltip content={getVisibilityTooltip(story.visibility)} position="top">
              <VisibilityBadge visibility={story.visibility}>
                {story.visibility.toLowerCase()}
              </VisibilityBadge>
            </Tooltip>
          )}
        </div>
        <Tooltip content={`${formatRating(story.rating)} average rating from ${story.ratingCount} ${story.ratingCount === 1 ? 'review' : 'reviews'}. Viewed ${story.viewCount} ${story.viewCount === 1 ? 'time' : 'times'}.`} position="top">
          <StoryRating>
            ⭐ {formatRating(story.rating)} ({story.ratingCount}) • 👁️ {story.viewCount}
          </StoryRating>
        </Tooltip>
      </StoryMeta>
      
      {showActions && (onEdit || onDelete) && (
        <ActionsContainer>
          {onEdit && (
            <Tooltip content="Edit this story" position="top">
              <ActionButton onClick={handleEdit}>
                {t('common.edit')}
              </ActionButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip content="Permanently delete this story" position="top">
              <ActionButton variant="danger" onClick={handleDelete}>
                {t('common.delete')}
              </ActionButton>
            </Tooltip>
          )}
        </ActionsContainer>
      )}
    </CardContainer>
  );
};

export default StoryCard;